import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import {
  Card,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import Icon from "@mui/material/Icon";

// IDP components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Services
import apiClient from "services/api";

function DocumentTypeEditor() {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [openEditor, setOpenEditor] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    prompt_template: "",
    schema: "",
  });

  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const TabPanel = ({ children, value, index, ...other }) => {
    TabPanel.propTypes = {
      children: PropTypes.node.isRequired,
      value: PropTypes.number.isRequired,
      index: PropTypes.number.isRequired,
    };

    return (
      <Box
        role="tabpanel"
        hidden={value !== index}
        id={`template-tabpanel-${index}`}
        aria-labelledby={`template-tab-${index}`}
        sx={{ pt: 2 }}
        {...other}
      >
        {value === index && children}
      </Box>
    );
  };

  const a11yProps = (index) => ({
    id: `template-tab-${index}`,
    "aria-controls": `template-tabpanel-${index}`,
  });

  useEffect(() => {
    const fetchDocumentTypes = async () => {
      try {
        const types = await apiClient.getDocumentTypes();
        setDocumentTypes(types);
      } catch (error) {
        console.error("Error fetching document types:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocumentTypes();
  }, []);

  useEffect(() => {
    if (selectedType) {
      const docType = documentTypes.find((type) => type.id === selectedType);
      if (docType) {
        setFormData({
          name: docType.name,
          description: docType.description || "",
          prompt_template: docType.prompt_template || "",
          schema: JSON.stringify(docType.output_schema || {}, null, 2),
        });
      }
    }
  }, [selectedType, documentTypes]);

  const handleSave = async () => {
    try {
      const docTypeData = {
        name: formData.name,
        description: formData.description,
        prompt_template: formData.prompt_template,
        output_schema: JSON.parse(formData.schema),
      };

      if (selectedType) {
        // Update existing document type
        await apiClient.updateDocumentType(selectedType, docTypeData);
      } else {
        // Create new document type
        await apiClient.createDocumentType(docTypeData);
      }

      setEditMode(false);
      setOpenEditor(false);

      // Refresh document types
      const types = await apiClient.getDocumentTypes();
      setDocumentTypes(types);
    } catch (error) {
      console.error("Error saving document type:", error);
    }
  };

  const handleAddNew = () => {
    setSelectedType("");
    setFormData({
      name: "",
      description: "",
      prompt_template: "",
      schema: "{}",
    });
    setEditMode(true);
    setOpenEditor(true);
  };

  const handleEdit = (id) => {
    setSelectedType(id);
    setEditMode(true);
    setOpenEditor(true);
  };

  const handleDeleteClick = (id) => {
    setTypeToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiClient.deleteDocumentType(typeToDelete);
      setDeleteDialogOpen(false);
      setTypeToDelete(null);
      // Refresh document types
      const types = await apiClient.getDocumentTypes();
      setDocumentTypes(types);
    } catch (error) {
      console.error("Error deleting document type:", error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTypeToDelete(null);
  };

  // Action component for the table
  const Action = ({ id }) => (
    <MDBox display="flex" alignItems="center">
      <MDBox mr={1}>
        <Tooltip title="Edit Document Type">
          <Icon
            fontSize="small"
            color="inherit"
            onClick={() => handleEdit(id)}
            sx={{ cursor: "pointer" }}
          >
            edit
          </Icon>
        </Tooltip>
      </MDBox>
      <Tooltip title="Delete Document Type">
        <Icon
          fontSize="small"
          color="error"
          onClick={() => handleDeleteClick(id)}
          sx={{ cursor: "pointer" }}
        >
          delete
        </Icon>
      </Tooltip>
    </MDBox>
  );

  Action.propTypes = {
    id: PropTypes.string.isRequired,
  };

  // Table data
  const tableData = {
    columns: [
      { Header: "name", accessor: "name", width: "30%", align: "left" },
      { Header: "description", accessor: "description", align: "left" },
      { Header: "created at", accessor: "createdAt", align: "center" },
      { Header: "updated at", accessor: "updatedAt", align: "center" },
      { Header: "action", accessor: "action", align: "center" },
    ],
    rows: loading
      ? [
          {
            name: "Loading...",
            description: "--",
            createdAt: "--",
            updatedAt: "--",
            action: <Action id="loading" />,
          },
        ]
      : documentTypes.map((type) => ({
          name: type.name || "Untitled",
          description: type.description || "--",
          createdAt: type.created_at ? new Date(type.created_at).toLocaleString() : "--",
          updatedAt: type.updated_at ? new Date(type.updated_at).toLocaleString() : "--",
          action: <Action id={type.id ? type.id.toString() : "0"} />,
        })) || [],
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Document Types List
                </MDTypography>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={tableData}
                  isSorted={true}
                  entriesPerPage={{
                    defaultValue: 10,
                    entries: [5, 10, 25, 50],
                  }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                />
                <MDBox display="flex" justifyContent="flex-end" mt={2} pb={3} pr={2}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={handleAddNew}
                    startIcon={<Icon>add</Icon>}
                  >
                    Add New
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={openEditor} onClose={() => setOpenEditor(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedType ? "Edit Document Type" : "Add New Document Type"}</DialogTitle>
        <DialogContent>
          <MDBox p={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <Tabs
                      value={activeTab}
                      onChange={handleTabChange}
                      variant="fullWidth"
                      textColor="primary"
                      indicatorColor="primary"
                      aria-label="document type configuration tabs"
                    >
                      <Tab label="Prompt Template" {...a11yProps(0)} />
                      <Tab label="Schema (JSON)" {...a11yProps(1)} />
                    </Tabs>
                  </Box>
                  <TabPanel value={activeTab} index={0}>
                    <TextField
                      fullWidth
                      label="Prompt Template"
                      defaultValue={formData.prompt_template}
                      onBlur={(e) => setFormData({ ...formData, prompt_template: e.target.value })}
                      multiline
                      rows={12}
                      helperText="Use {document_text} as placeholder for the document content"
                      sx={{ backgroundColor: "background.paper" }}
                    />
                  </TabPanel>
                  <TabPanel value={activeTab} index={1}>
                    <TextField
                      fullWidth
                      label="Schema (JSON)"
                      defaultValue={formData.schema}
                      onBlur={(e) => {
                        const newValue = e.target.value;
                        setFormData({ ...formData, schema: newValue });
                      }}
                      multiline
                      rows={12}
                      error={(() => {
                        try {
                          JSON.parse(formData.schema);
                          return false;
                        } catch {
                          return true;
                        }
                      })()}
                      helperText={(() => {
                        try {
                          JSON.parse(formData.schema);
                          return "Valid JSON schema";
                        } catch (e) {
                          return "Invalid JSON format";
                        }
                      })()}
                      sx={{ backgroundColor: "background.paper" }}
                    />
                  </TabPanel>
                </Box>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDBox display="flex" justifyContent="flex-end">
            <MDButton variant="gradient" color="secondary" onClick={() => setOpenEditor(false)}>
              Cancel
            </MDButton>
          </MDBox>
          <MDBox display="flex" justifyContent="flex-end">
            <MDButton variant="gradient" color="info" onClick={handleSave}>
              Save
            </MDButton>
          </MDBox>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Document Type</DialogTitle>
        <DialogContent>
          <MDTypography variant="body1" id="delete-dialog-description">
            Are you sure you want to delete this document type? This action cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default DocumentTypeEditor;
